import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  listEmailActivities,
  listNoteActivities,
  listSmsActivities,
  listActivitiesAll,
  createNote,
  getNoteDetail,
  updateNote,
  deleteNote,
} from "../../../../services/activity";

const ActivityContext = createContext(null);

const ACTIVITY_TYPES = {
  ALL: "all",
  EMAIL: "email",
  SMS: "sms",
  NOTES: "notes",
};

const initialCollectionState = {
  items: [],
  meta: {
    total_items: 0,
    total_pages: 1,
    current_page: 1,
    per_page: 20,
    has_next: false,
    has_previous: false,
    next_cursor: null,
  },
  filters: {},
  search: {},
  orderBy: ["created_at"],
  orderDirection: ["DESC"],
  loading: false,
  error: null,
};

const initialState = {
  [ACTIVITY_TYPES.ALL]: { ...initialCollectionState },
  [ACTIVITY_TYPES.EMAIL]: { ...initialCollectionState },
  [ACTIVITY_TYPES.SMS]: { ...initialCollectionState },
  [ACTIVITY_TYPES.NOTES]: { ...initialCollectionState },
  activeTab: ACTIVITY_TYPES.ALL,
};

const activityServiceMap = {
  [ACTIVITY_TYPES.ALL]: listActivitiesAll,
  [ACTIVITY_TYPES.EMAIL]: listEmailActivities,
  [ACTIVITY_TYPES.SMS]: listSmsActivities,
  [ACTIVITY_TYPES.NOTES]: listNoteActivities,
};

export const ActivityProvider = ({ children }) => {
  const [state, setState] = useState(initialState);

  const setActiveTab = useCallback((tab) => {
    setState((prev) => ({
      ...prev,
      activeTab: tab,
    }));
  }, []);

  const updateCollectionState = useCallback((type, updates) => {
    setState((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        ...updates,
      },
    }));
  }, []);

  const removeNoteOptimistically = useCallback((noteUuid) => {
    setState((prev) => {
      const notesCollection = prev[ACTIVITY_TYPES.NOTES];
      const allCollection = prev[ACTIVITY_TYPES.ALL];
      
      const updatedNotesItems = (notesCollection.items || []).filter(
        (item) => {
          const itemUuid = 
            item.note_uuid ||
            item.uuid ||
            item.raw?.note_uuid ||
            item.raw?.uuid ||
            item.raw?.id ||
            item.raw?.noteId ||
            item.raw?.activity_uuid;
          return itemUuid !== noteUuid;
        }
      );

      const updatedAllItems = (allCollection.items || []).filter((item) => {
        if (item.type === "Notes") {
          const itemUuid =
            item.data?.note_uuid ||
            item.data?.uuid ||
            item.data?.raw?.note_uuid ||
            item.data?.raw?.uuid ||
            item.data?.raw?.id ||
            item.data?.raw?.noteId ||
            item.data?.raw?.activity_uuid;
          return itemUuid !== noteUuid;
        }
        return true;
      });

      return {
        ...prev,
        [ACTIVITY_TYPES.NOTES]: {
          ...notesCollection,
          items: updatedNotesItems,
          meta: {
            ...notesCollection.meta,
            total_items: Math.max(0, (notesCollection.meta?.total_items || 0) - 1),
          },
        },
        [ACTIVITY_TYPES.ALL]: {
          ...allCollection,
          items: updatedAllItems,
        },
      };
    });
  }, []);

  // Track in-flight requests to prevent duplicate simultaneous calls
  const inFlightRequestsRef = useRef(new Map());

  const fetchActivity = useCallback(
    async (type, params = {}) => {
      const collectionType =
        ACTIVITY_TYPES[type?.toUpperCase?.()] || ACTIVITY_TYPES.ALL;
      const service = activityServiceMap[collectionType];

      if (!service) {
        console.warn(`No service defined for activity type "${type}".`);
        return null;
      }

      // Create a unique key for this request
      const requestKey = `${collectionType}-${JSON.stringify(params)}`;
      
      // Check if we're already loading this exact request
      if (inFlightRequestsRef.current.has(requestKey)) {
        // Return the existing promise
        return inFlightRequestsRef.current.get(requestKey);
      }

      // Loading flags
      if (collectionType === ACTIVITY_TYPES.ALL) {
        updateCollectionState(ACTIVITY_TYPES.EMAIL, {
          loading: true,
          error: null,
        });
        updateCollectionState(ACTIVITY_TYPES.SMS, {
          loading: true,
          error: null,
        });
        updateCollectionState(ACTIVITY_TYPES.NOTES, {
          loading: true,
          error: null,
        });
      } else {
        updateCollectionState(collectionType, { loading: true, error: null });
      }

      // Create the request promise and store it
      const requestPromise = (async () => {
        try {
          const response = await service(params);

          // Distribute when it is ALL (a single request)
          if (collectionType === ACTIVITY_TYPES.ALL) {
            const { email = [], sms = [], note = [] } = response.body || {};

            updateCollectionState(ACTIVITY_TYPES.EMAIL, {
              items: email,
              meta: { ...initialCollectionState.meta, total_items: email.length },
              filters: params.filters || {},
              search: params.search || {},
              orderBy: params.order_by || ["last_update"],
              orderDirection: params.order_direction || ["DESC"],
              loading: false,
              error: null,
            });
            updateCollectionState(ACTIVITY_TYPES.SMS, {
              items: sms,
              meta: { ...initialCollectionState.meta, total_items: sms.length },
              filters: params.filters || {},
              search: params.search || {},
              orderBy: params.order_by || ["last_update"],
              orderDirection: params.order_direction || ["DESC"],
              loading: false,
              error: null,
            });
            updateCollectionState(ACTIVITY_TYPES.NOTES, {
              items: note,
              meta: { ...initialCollectionState.meta, total_items: note.length },
              filters: params.filters || {},
              search: params.search || {},
              orderBy: params.order_by || ["last_update"],
              orderDirection: params.order_direction || ["DESC"],
              loading: false,
              error: null,
            });

            return response;
          }

          // Individual tabs
          const normalizedMeta = {
            total_items:
              response.meta?.total_items ??
              (Array.isArray(response.body) ? response.body.length : 0),
            total_pages:
              response.meta?.total_pages ??
              Math.max(
                1,
                Math.ceil(
                  (Array.isArray(response.body) ? response.body.length : 0) /
                    (response.meta?.per_page ?? 20)
                )
              ),
            current_page: response.meta?.current_page ?? 1,
            per_page: response.meta?.per_page ?? 20,
            has_next: response.meta?.has_next ?? false,
            has_previous: response.meta?.has_previous ?? false,
            next_cursor: response.meta?.next_cursor ?? null,
          };

          updateCollectionState(collectionType, {
            items: response.body || [],
            meta: normalizedMeta,
            filters: params.filters || {},
            search: params.search || {},
            orderBy: params.order_by || ["created_at"],
            orderDirection: params.order_direction || ["DESC"],
            loading: false,
            error: null,
          });

          return response;
        } catch (error) {
          console.error(
            `Error fetching ${collectionType} activity:`,
            error?.response?.data || error?.message,
            { params }
          );

          if (collectionType === ACTIVITY_TYPES.ALL) {
            updateCollectionState(ACTIVITY_TYPES.EMAIL, {
              items: [],
              loading: false,
              error,
            });
            updateCollectionState(ACTIVITY_TYPES.SMS, {
              items: [],
              loading: false,
              error,
            });
            updateCollectionState(ACTIVITY_TYPES.NOTES, {
              items: [],
              loading: false,
              error,
            });
            return null;
          }

          updateCollectionState(collectionType, {
            items: [],
            loading: false,
            error,
          });
          return null;
        } finally {
          // Always remove from tracking when done (success or error)
          inFlightRequestsRef.current.delete(requestKey);
        }
      })();

      // Store the promise in the map
      inFlightRequestsRef.current.set(requestKey, requestPromise);

      return requestPromise;
    },
    [updateCollectionState]
  );

  const clearActivityData = useCallback(() => {
    setState(initialState);
    // Clear in-flight requests tracking
    inFlightRequestsRef.current.clear();
  }, []);

  const createNoteActivity = useCallback(createNote, []);
  
  const getNoteDetailActivity = useCallback(getNoteDetail, []);

  const updateNoteActivity = useCallback(updateNote, []);

  const deleteNoteActivity = useCallback(deleteNote, []);

  const value = useMemo(
    () => ({
      state,
      activeTab: state.activeTab,
      collections: {
        [ACTIVITY_TYPES.ALL]: state[ACTIVITY_TYPES.ALL],
        [ACTIVITY_TYPES.EMAIL]: state[ACTIVITY_TYPES.EMAIL],
        [ACTIVITY_TYPES.SMS]: state[ACTIVITY_TYPES.SMS],
        [ACTIVITY_TYPES.NOTES]: state[ACTIVITY_TYPES.NOTES],
      },
      setActiveTab,
      fetchActivity,
      clearActivityData,
      createNoteActivity,
      getNoteDetailActivity,
      updateNoteActivity,
      deleteNoteActivity,
      removeNoteOptimistically,
    }),
    [state, setActiveTab, fetchActivity, clearActivityData, createNoteActivity, getNoteDetailActivity, updateNoteActivity, deleteNoteActivity, removeNoteOptimistically]
  );

  return (
    <ActivityContext.Provider value={value}>
      {children}
    </ActivityContext.Provider>
  );
};

export const useActivityContext = () => {
  const context = useContext(ActivityContext);
  if (!context) {
    throw new Error("useActivityContext must be used within ActivityProvider");
  }
  return context;
};

export { ACTIVITY_TYPES };
